import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import MotionProvider from '@/components/MotionProvider';
import Home from '@/app/page';

const ArtistStoryPage = lazy(() => import('@/app/about/page'));
const PrivacyPage = lazy(() => import('@/app/privacy/page'));
const SystemOverviewPage = lazy(() => import('@/app/system/page'));
const LoopyProPage = lazy(() => import('@/app/system/loopy-pro/page'));
const AbletonControlPage = lazy(() => import('@/app/system/ableton-control/page'));

const routeMeta: Record<string, { title: string; description: string }> = {
    '/': {
        title: 'DARK TR0JAN | Music - Live Looping & Piano Performances',
        description: 'Live looping, piano and layered performances by DARK TR0JAN.',
    },
    '/about/': {
        title: 'Artist Story | DARK TR0JAN',
        description: 'The story behind DARK TR0JAN: piano by ear, game soundtrack arrangements, live looping and music production.',
    },
    '/privacy/': {
        title: 'Privacy Policy | DARK TR0JAN',
        description: 'How DARK TR0JAN handles privacy, embedded media and cookie consent.',
    },
    '/system/': {
        title: 'Performance System | DARK TR0JAN',
        description: 'An overview of the instruments, looping tools, automation and control layers behind DARK TR0JAN performances.',
    },
    '/system/loopy-pro/': {
        title: 'Cameras and Synced Visuals | DARK TR0JAN',
        description: 'How camera coverage and music-driven visuals connect to DARK TR0JAN studio livestreams.',
    },
    '/system/ableton-control/': {
        title: 'Software and Signal Routing | DARK TR0JAN',
        description: 'How Auracle X, Ableton Live, Loopy Pro, Bome and physical controllers connect the performance rig.',
    },
};

function normalisePath(pathname: string) {
    return pathname === '/' ? '/' : `${pathname.replace(/\/+$/, '')}/`;
}

function RouteEffects() {
    const location = useLocation();

    useEffect(() => {
        const pathname = normalisePath(location.pathname);
        const meta = routeMeta[pathname];
        if (meta) {
            document.title = meta.title;
            document.querySelector('meta[name="description"]')?.setAttribute('content', meta.description);
            document.querySelector('link[rel="canonical"]')?.setAttribute(
                'href',
                `https://darktrojanmusic.com${pathname}`,
            );
        }

        if (!location.hash) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            return;
        }

        const id = decodeURIComponent(location.hash.slice(1));
        let attempts = 0;
        let timer = 0;
        const findTarget = () => {
            const target = document.getElementById(id);
            if (target) {
                target.scrollIntoView({ behavior: 'auto', block: 'start' });
                return;
            }
            attempts += 1;
            if (attempts < 40) timer = window.setTimeout(findTarget, 75);
        };
        timer = window.setTimeout(findTarget, 0);
        return () => window.clearTimeout(timer);
    }, [location.hash, location.pathname]);

    return null;
}

function NotFound() {
    return (
        <main className="flex min-h-screen items-center justify-center bg-[#030208] px-6 text-center text-white">
            <div>
                <p className="section-eyebrow mb-5">404 / Signal lost</p>
                <h1 className="section-heading text-5xl">Page not found</h1>
                <a href="/" className="neon-button mt-8">Return home</a>
            </div>
        </main>
    );
}

function RouteFallback() {
    return <main className="min-h-screen bg-[#030208]" aria-label="Loading page" />;
}

export default function App() {
    return (
        <MotionProvider>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about/" element={<ArtistStoryPage />} />
                    <Route path="/privacy/" element={<PrivacyPage />} />
                    <Route path="/system/" element={<SystemOverviewPage />} />
                    <Route path="/system/loopy-pro/" element={<LoopyProPage />} />
                    <Route path="/system/ableton-control/" element={<AbletonControlPage />} />
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
            <RouteEffects />
        </MotionProvider>
    );
}
